import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { candidateCellColumn } from './CandidateCell';
import { contactWaitingColumn } from './ContactWaitingCell';
import ContactStatusIcons from './ContactStatusIcons';
import RowActionsCell from './RowActionsCell';
import { filterActionsForRow } from '../actionConditions';
import SelectableTabTable from './SelectableTabTable';
import PipelineDataTable, { candidateShape } from './PipelineDataTable';
import TabEmptyState from './TabEmptyState';
import { registerTab } from './tabRegistry';

// senior_level_flow — "Contacted" tab (shown to the TA as "Contact", per
// the flow-statuses API's own tabs[].name). Also covers the "Acknowledged"
// sub-status — tabs[].filters.status for this tab is
// ["Contacted", "Acknowledged"] — it doesn't get its own tab, same as Non
// Responsive under the default flow. manual_actions: interview_profile_update,
// mark_not_interested, resend_contact_link, acknowledge_contact,
// mark_as_non_responsive.
//
// Bespoke rather than built off seniorFlowTabFactory's shared shape — same
// move PendingTab.jsx already made for the `default` flow's one-off email-
// status column, and for the same reason: a column this specific (the
// per-channel contact-status icons below) shouldn't get branched into the
// factory every other senior-flow tab also runs through. Otherwise mirrors
// the factory's own selectable/not-selectable shape exactly (see its own
// comment for why): SelectableTabTable when every visible row shares one
// real status, plain PipelineDataTable + a per-row status line (inside
// RowActionsCell — see its own `rowStatus` comment) when "All statuses"
// mixes Contacted and Acknowledged rows together.
//
// Contact Status column reads the real row.notifications.status.email
// flags directly (see contactStatus.js and ContactStatusIcons.jsx for the
// full contract) — no mock fallback needed now that the backend ships the
// real field.
const SeniorContactedTab = ({
  candidates,
  isLoading,
  page,
  pageSize,
  totalPages,
  totalItems,
  onPageChange,
  onPageSizeChange,
  opportunityId,
  assignmentId,
  requirementLocationId,
  stateMachineKey,
  manualActions,
  manualActionsByStatus,
  selectable,
  onActionSuccess,
  onViewProfile,
}) => {
  const columns = useMemo(
    () => [
      candidateCellColumn,
      {
        key: 'contact_status',
        header: 'Contact Status',
        className: 'text-sm',
        // Fixed, not left to table-fixed's equal-share default — see
        // sharedColumns.js' own comment on why every non-identity column
        // here needs an explicit width. 80px (just enough for the 24px
        // chip) looked right for the chip itself but was narrower than the
        // "Contact Status" header text, which would overflow at that
        // width — 150px is wide enough that the label never overflows.
        width: '150px',
        // Left-aligned per explicit request — every senior_level_flow
        // column reads left-to-right like the Candidate column.
        // ContactStatusIcons renders a plain inline-level button (no
        // internal flex row to fight this), so the column's own
        // `textAlign` is all that's needed here.
        align: 'left',
        render: (_v, row) => (
          <ContactStatusIcons row={row} onActionSuccess={onActionSuccess} />
        ),
      },
      contactWaitingColumn,
      {
        key: 'row_actions',
        header: '',
        // `relative` makes this <td> the containing block for
        // RowActionsCell's absolutely positioned status badge (rowStatus) —
        // see that file's own comment.
        className: 'text-sm relative',
        // The primary action button (whichever manual_action the row's
        // status flags primary_action: true, rendered outside the kebab —
        // see RowActionsCell.jsx) + kebab + chevron needs real room — left
        // unconstrained it would only get an equal share alongside every
        // other column under table-fixed, same issue as the rest. 210px was
        // enough back when this slot only ever held "View profile", but
        // this tab's own interview_profile_update action ("Interview
        // Profile Update") overflowed into the next column's cell at that
        // width — confirmed live. Matches seniorFlowTabFactory.jsx's own
        // row_actions width for consistency across every senior_level_flow
        // tab.
        width: '280px',
        // Left-aligned per explicit request — every senior_level_flow
        // column reads left-to-right like the Candidate column.
        // RowActionsCell's own flex row still renders flush right
        // internally (justify-end) regardless of this column-level
        // setting.
        align: 'left',
        render: (_v, row) => (
          <RowActionsCell
            candidateId={row.candidate_id}
            mappingId={row.id}
            status="Contacted"
            // Only in a merged "All statuses" view — see
            // seniorFlowTabFactory.jsx and RowActionsCell.jsx's own comment
            // on `rowStatus` vs `status`.
            rowStatus={selectable ? undefined : row.status}
            opportunityId={opportunityId}
            assignmentId={assignmentId}
            requirementLocationId={requirementLocationId}
            stateMachineKey={stateMachineKey}
            manualActions={filterActionsForRow(
              manualActionsByStatus[row.status] || manualActions,
              row
            )}
            onActionSuccess={onActionSuccess}
            onViewProfile={onViewProfile}
          />
        ),
      },
    ],
    [
      selectable,
      opportunityId,
      assignmentId,
      requirementLocationId,
      stateMachineKey,
      manualActions,
      manualActionsByStatus,
      onActionSuccess,
      onViewProfile,
    ]
  );

  const emptyState = (
    <TabEmptyState description="No candidates are waiting on contact right now." />
  );

  if (!selectable) {
    return (
      <PipelineDataTable
        columns={columns}
        candidates={candidates}
        isLoading={isLoading}
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        emptyState={emptyState}
      />
    );
  }

  return (
    <SelectableTabTable
      columns={columns}
      candidates={candidates}
      isLoading={isLoading}
      page={page}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      opportunityId={opportunityId}
      stateMachineKey={stateMachineKey}
      manualActions={manualActions}
      onActionSuccess={onActionSuccess}
      emptyState={emptyState}
    />
  );
};

SeniorContactedTab.propTypes = {
  candidates: PropTypes.arrayOf(candidateShape),
  isLoading: PropTypes.bool,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  totalPages: PropTypes.number,
  totalItems: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  opportunityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  assignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  requirementLocationId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  stateMachineKey: PropTypes.string,
  manualActions: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })
  ),
  manualActionsByStatus: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })
    )
  ),
  selectable: PropTypes.bool,
  onActionSuccess: PropTypes.func,
  onViewProfile: PropTypes.func,
};

SeniorContactedTab.defaultProps = {
  candidates: [],
  isLoading: false,
  page: 1,
  pageSize: 10,
  totalPages: 1,
  totalItems: 0,
  onPageChange: () => {},
  onPageSizeChange: () => {},
  opportunityId: undefined,
  assignmentId: undefined,
  requirementLocationId: undefined,
  stateMachineKey: 'senior_level_flow',
  manualActions: [],
  manualActionsByStatus: {},
  selectable: true,
  onActionSuccess: () => {},
  onViewProfile: () => {},
};

registerTab({
  stateMachineKey: 'senior_level_flow',
  state: 'Contacted',
  component: SeniorContactedTab,
});

export default SeniorContactedTab;
